import { type AiAssistantSource } from './types';

export type AssistantSourceGroup = {
  key: string;
  numbers: number[];
  sources: AiAssistantSource[];
};

function citationNumberForSource(
  source: AiAssistantSource,
  index: number
): number | undefined {
  if (source.citationNumber != null) {
    return source.citationNumber;
  }
  // Reply markdown links are listed without citation numbers.
  if (source.kind === 'page' && source.fromReply) {
    return undefined;
  }
  return index + 1;
}

function sourceGroupKey(source: AiAssistantSource, index: number): string {
  if (source.kind === 'page') {
    return `page:${source.to}`;
  }
  if (source.documentId) {
    return `document:${source.documentId}`;
  }
  if (source.file) {
    return `document-file:${source.file}`;
  }
  return `document-label:${source.label}:${index}`;
}

/** Group flat per-number sources into one card per document or page. */
export function groupAssistantSources(
  sources: readonly AiAssistantSource[]
): AssistantSourceGroup[] {
  const groups: AssistantSourceGroup[] = [];
  const byKey = new Map<string, AssistantSourceGroup>();

  for (const [index, source] of sources.entries()) {
    const number = citationNumberForSource(source, index);
    const key = sourceGroupKey(source, index);
    const existing = byKey.get(key);
    if (existing) {
      existing.sources.push(source);
      if (number != null) {
        existing.numbers.push(number);
      }
      continue;
    }
    const group: AssistantSourceGroup = {
      key,
      numbers: number == null ? [] : [number],
      sources: [source],
    };
    byKey.set(key, group);
    groups.push(group);
  }

  return groups;
}

export function unionGroupPages(
  group: AssistantSourceGroup
): number[] | undefined {
  const pages = group.sources.flatMap((source) =>
    source.kind === 'document' ? (source.pages ?? []) : []
  );
  return pages.length > 0 ? pages : undefined;
}

export function sourceForCitationNumber(
  group: AssistantSourceGroup,
  citationNumber: number
): AiAssistantSource {
  const index = group.numbers.indexOf(citationNumber);
  return group.sources[index] ?? group.sources[0];
}

/** Resolve a flat per-number source list to the passage for citation `n`. */
export function sourceByCitationNumber(
  sources: readonly AiAssistantSource[],
  citationNumber: number
): AiAssistantSource | undefined {
  return sources.find(
    (source, index) => (source.citationNumber ?? index + 1) === citationNumber
  );
}
